import { Option, InfoBanner } from '@de/types/types';
import { ConditionType, LogicType } from '@de/types/enums';
import { evaluateCondition } from '@de/utils/getNextStep';
import type { StepData } from '@de/providers/dynamicExperienceReducer';

export interface FilterInfoBannersArgs {
  configs: InfoBanner[];
  selectedOptions: Option[];
  stepData: (Partial<StepData> & Record<string, unknown>) | null | undefined;
}

/**
 * Filters and prioritizes info banner configs based on conditions.
 *
 * Priority:
 * 1. If any conditional banners match, returns **all** of them (no cap).
 * 2. Otherwise, returns at most one default banner (`slice(0, 1)`).
 *
 * UI layers should decide how many banners to render when multiple conditionals match.
 */
export const filterAndPrioritizeInfoBanners = ({
  configs,
  selectedOptions,
  stepData,
}: FilterInfoBannersArgs): InfoBanner[] => {
  if (configs.length === 0) {
    return [];
  }

  const defaultBanners = configs.filter(
    (config) =>
      !config.condition || config.condition.type === ConditionType.DEFAULT,
  );

  const conditionalBanners = configs.filter(
    (config) =>
      config.condition && config.condition.type !== ConditionType.DEFAULT,
  );

  const matchingConditionalBanners = conditionalBanners.filter((config) => {
    if (!config.condition) return false;
    if (selectedOptions.length === 0) return false;

    return evaluateCondition(
      {
        type: LogicType.NEXT_STEP,
        nextId: '',
        condition: config.condition,
      },
      stepData ?? {},
      selectedOptions,
    );
  });

  if (matchingConditionalBanners.length > 0) {
    return matchingConditionalBanners;
  }

  if (defaultBanners.length > 0) {
    return defaultBanners.slice(0, 1);
  }

  return [];
};
